import {Card, Col, Row} from "reactstrap";
import React, {useState, useMemo} from "react";
import {useTranslation} from "react-i18next";
import { ChangeDateFormat } from "../../Common/Helpers/ChangeDateFormat";
import { ToCustomDecimal } from "../../AppSystem/Helpers/ToCustomDecimal";
import { AttestHonorariumViews } from "../../Attest/Components/AttestHonorariumViews";
import { AttestReimbursementViews } from "../../Attest/Components/AttestReimbursementViews";
import { AttestPayViews } from "../../Attest/Components/AttestPayViews";
import { CheckUserRole } from "../../AppSystem/Helpers/CheckUserRole";
import { useSelector } from "react-redux";

export function EconomicReportDetailsView({data, modelId, itemFromState}) {
    const { rolesData } = useSelector(state => state.auth);
    const isSuperAdmin = CheckUserRole(rolesData, 'SuperAdmin');
    const { t } = useTranslation();
    const [moreInfo, setMoreInfo] = useState(true);
    data.amount = itemFromState?.amount || data.amount;

    const specificItem = useMemo(() => {
        if (!data?.modelInfo || !modelId) return null;

        if (data.modelInfo.fixeds && Array.isArray(data.modelInfo.fixeds)) {
            const fixedItem = data.modelInfo.fixeds.find(item => item.id === modelId);
            if (fixedItem) return { ...fixedItem, itemType: 'fixed' };
        }

        if (data.modelInfo.expenses && Array.isArray(data.modelInfo.expenses)) {
            if (data.modelInfo.expenses.length === 0) {
                return null;
            }

            const hasCompleteData = data.modelInfo.expenses.every(expense => 
                expense.amount !== undefined && expense.vatAmount !== undefined
            );
            
            if (!hasCompleteData) {
                return null;
            }

            let totalAmount = 0;
            let totalVatAmount = 0;
            
            for (const expense of data.modelInfo.expenses) {
                const amount = parseFloat(expense.amount) || 0;
                const vatAmount = parseFloat(expense.vatAmount) || 0;
                
                totalAmount += amount;
                totalVatAmount += vatAmount;
            }
            
            const expenseItem = data.modelInfo.expenses.find(item => item.id === modelId);
            
            if (expenseItem) {
                const updatedExpenseItem = { ...expenseItem };
                updatedExpenseItem.amount = itemFromState?.relatedReimbursementAmount || totalAmount;
                updatedExpenseItem.totalVatAmount = totalVatAmount;
                
                return { ...updatedExpenseItem, itemType: 'expense' };
            }
        }

        if (data.modelInfo.mileage && data.modelInfo.mileage.id === modelId) {
            return { ...data.modelInfo.mileage, itemType: 'mileage' };
        }

        return null;
    }, [
        data?.modelInfo, 
        modelId, 
        itemFromState?.relatedReimbursementAmount,
        data?.modelInfo?.expenses?.length,
        data?.modelInfo?.fixeds?.length,
        JSON.stringify(data?.modelInfo?.expenses),
        JSON.stringify(data?.modelInfo?.fixeds)
    ]);

    const displayData = specificItem || data;

    return(
        <>
            <Col lg={12} className="pb-5 px-0">
                <Card className="card-backend p-4">
                    <Row>
                        <Col xs={12} lg={6}>
                            <h2 className="rokkitt-font">{t('economicReport.economicReportInfo')}</h2>
                        </Col>
                        {data !== null ?
                            <Col lg={12}>
                                <Card className="p-3">
                                    <Row className="px-2 px-lg-4">
                                        <Col xs={12} lg={8}>
                                            <ul className="list-unstyled">
                                                {/* Show visualId - use the specific item's if it exists */}
                                                {displayData.visualId !== "" && displayData.visualId !== undefined ?
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.id')}</span>
                                                        <span className="col-6 col-lg-9">{displayData.visualId}</span>
                                                    </li>
                                                : ""}
                                                
                                                {/* SuperAdmin information */}
                                                {isSuperAdmin === true ?
                                                    <>
                                                        <li className="pt-2 row">
                                                            <span className="fw-bold col-6 col-lg-3">{t('modelLabel.id')}</span>
                                                            <span className="col-6 col-lg-9">{displayData.id}</span>
                                                        </li>
                                                        <li className="pt-2 row">
                                                            <span className="fw-bold col-6 col-lg-3">{t('modelLabel.orgId')}</span>
                                                            <span className="col-6 col-lg-9">{displayData.orgId}</span>
                                                        </li>
                                                    </>
                                                    : ''}

                                                {/* Organization name */}
                                                {(displayData.orgName || data.modelInfo?.orgName) && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.orgName')}</span>
                                                        <span className="col-6 col-lg-9">{displayData.orgName || data.modelInfo.orgName}</span>
                                                    </li>
                                                )}

                                                {/* Organization type */}
                                                {data.orgType && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.type')}</span>
                                                        <span className="col-6 col-lg-9">{data.orgType}</span>
                                                    </li>
                                                )}

                                                {/* Cost center */}
                                                {data.costPlace && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.KST')}</span>
                                                        <span className="col-6 col-lg-9">{data.costPlace}</span>
                                                    </li>
                                                )}

                                                {/* Creation date */}
                                                <li className="pt-2 row">
                                                    <span className="fw-bold col-6 col-lg-3">{t('modelLabel.createdAt')}</span>
                                                    <span className="col-6 col-lg-9">{ChangeDateFormat('dateTime', displayData.createdAt || data.createdAt)}</span>
                                                </li>

                                                {/* Created by (ID) */}
                                                {isSuperAdmin === true && (displayData.userId || data.modelInfo?.userId || data.createdBy) ? (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.createdBy')}</span>
                                                        <span className="col-6 col-lg-9">
                                                            {displayData.userId || data.modelInfo?.userId || data.createdBy || ""}
                                                        </span>
                                                    </li>
                                                ) : ''}

                                                {/* Creator name */}
                                                <li className="pt-2 row">
                                                    <span className="fw-bold col-6 col-lg-3">{t('modelLabel.creatorName')}</span>
                                                    <span className="col-6 col-lg-9">{displayData.userName || (data.createdByName ?? data.modelInfo?.createdByName ?? "")}</span>
                                                </li>

                                                {/* Specific information for mileage item type */}
                                                {specificItem && specificItem.itemType === 'mileage' && (
                                                    <>
                                                        <li className="pt-2 row">
                                                            <span className="fw-bold col-6 col-lg-3">{t('modelLabel.from')}</span>
                                                            <span className="col-6 col-lg-9">{specificItem.from}</span>
                                                        </li>
                                                        <li className="pt-2 row">
                                                            <span className="fw-bold col-6 col-lg-3">{t('modelLabel.to')}</span>
                                                            <span className="col-6 col-lg-9">{specificItem.to}</span>
                                                        </li>
                                                        <li className="pt-2 row">
                                                            <span className="fw-bold col-6 col-lg-3">{t('modelLabel.km')}</span>
                                                            <span className="col-6 col-lg-9">{specificItem.km}</span>
                                                        </li>
                                                    </>
                                                )}

                                                {/* Show type if it's a specific item */}
                                                {specificItem && specificItem.type && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.type')}</span>
                                                        <span className="col-6 col-lg-9">{specificItem.type}</span>
                                                    </li>
                                                )}

                                                {/* Amount */}
                                                {displayData.amount && displayData.amount !== 0 ?
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.amount')}</span>
                                                        <span className="col-6 col-lg-9">{ToCustomDecimal(displayData.amount)} kr</span>
                                                    </li>
                                                : null}

                                                {/* VAT Amount - Mostrar totalVatAmount si existe */}
                                                {(displayData.vatAmount || displayData.totalVatAmount) && (displayData.vatAmount !== 0 || displayData.totalVatAmount !== 0) ?
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.vatAmount')}</span>
                                                        <span className="col-6 col-lg-9">{ToCustomDecimal(displayData.totalVatAmount || displayData.vatAmount)} kr</span>
                                                    </li>
                                                : null}

                                                {/* Total amount (for mileage) */}
                                                {specificItem && specificItem.totalAmount && specificItem.totalAmount !== displayData.amount && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.totalAmount')}</span>
                                                        <span className="col-6 col-lg-9">{ToCustomDecimal(specificItem.totalAmount)} kr</span>
                                                    </li>
                                                )}

                                                {/* Net amount */}
                                                {specificItem && specificItem.net && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.net')}</span>
                                                        <span className="col-6 col-lg-9">{ToCustomDecimal(specificItem.net)} kr</span>
                                                    </li>
                                                )}

                                                {/* Income tax */}
                                                {specificItem && specificItem.incomeTax && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.incomeTax')}</span>
                                                        <span className="col-6 col-lg-9">{ToCustomDecimal(specificItem.incomeTax)} kr</span>
                                                    </li>
                                                )}

                                                {/* Employment tax */}
                                                {specificItem && specificItem.employmentTax && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.employmentTax')}</span>
                                                        <span className="col-6 col-lg-9">{ToCustomDecimal(specificItem.employmentTax)} kr</span>
                                                    </li>
                                                )}

                                                {/* Status */}
                                                {(data.modelInfo?.status || data.status) && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.status')}</span>
                                                        <span className="col-6 col-lg-9">
                                                            {(() => {
                                                                const status = data.modelInfo?.status || data.status;

                                                                if (status === "Nekad") return t('attest.denied');
                                                                if (status === "Godkänd") return t("attest.approved");
                                                                if (status === "Utbetald") return t("modelLabel.paidOut");
                                                                return status;
                                                            })()}
                                                        </span>
                                                    </li>
                                                )}

                                                {/* Unified Description */}
                                                {(
                                                    (specificItem && specificItem.description) ||
                                                    (!specificItem && (
                                                        data.model === "Förrättning" && data.modelInfo?.purpose ||
                                                        data.modelInfo?.description ||
                                                        data.description
                                                    ))
                                                ) && (
                                                    <li className="pt-4 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.description')}</span>
                                                        <span className="col-6 col-lg-9">
                                                            {specificItem?.description ? specificItem.description :
                                                                data.model === "Förrättning" && data.modelInfo?.purpose ? data.modelInfo.purpose :
                                                                data.modelInfo?.description ? data.modelInfo.description :
                                                                data.description === 'Användarlön' ? t('modelLabel.userSalary') :
                                                                data.description === 'Innehållen skatt' ? t('modelLabel.incomeTax') :
                                                                data.description === 'Arbetsgivaravgift' ? t('modelLabel.employmentTax') :
                                                                data.description
                                                            }
                                                        </span>
                                                    </li>
                                                )}

                                                {/* Purpose (for specific items) */}
                                                {specificItem && specificItem.purpose && (
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('reimbursement.purpose')}</span>
                                                        <span className="col-6 col-lg-9">{specificItem.purpose}</span>
                                                    </li>
                                                )}

                                                {/* Message */}
                                                {data.message && data.message !== "" ?
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('modelLabel.message')}</span>
                                                        <span className="col-6 col-lg-9">{data.message}</span>

                                                    </li>
                                                : ""}
                                                {data.type && data.type !== "" ?
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('internalTransaction.type')}</span>
                                                        <span className="col-6 col-lg-9">{data.type}</span>
                                                    </li>
                                                : ""}
                                                {data.subtype && data.subtype !== "" ?
                                                    <li className="pt-2 row">
                                                        <span className="fw-bold col-6 col-lg-3">{t('internalTransaction.subtype')}</span>
                                                        <span className="col-6 col-lg-9">{data.subtype}</span>
                                                    </li>
                                                : ""}
                                            </ul>
                                        </Col>
                                    </Row>
                                    {moreInfo === true ?
                                        <>
                                            {data.model === "Arvode" ?
                                                <AttestHonorariumViews data={data.modelInfo} modelId={data.modelId}/>
                                            : ""}
                                            {data.model === "Förrättning" ?
                                                <AttestReimbursementViews data={data.modelInfo} modelId={data.modelId}/>
                                            : ""}
                                            {data.model === "Betala" ?
                                                <AttestPayViews data={data.modelInfo} modelId={data.modelId}/>
                                            : ""}
                                        </>
                                    : ''}
                                </Card>
                            </Col>
                        : ''}
                    </Row>
                </Card>
            </Col>

        </>
    );
}